/**
 * 🦴 **POSING A SKINNED ANIMAL** — the bridge between `quadrig.ts`'s anatomy and
 * three.js's bones.
 *
 * 🔴 **WHY THIS FILE EXISTS AND `legsplit.ts` NO LONGER DOES THE JOB.** Until
 * 2026-09-23 a creature's body was **cut into rigid parts** and each part swung
 * about a pivot. 🎯 **haugur diagnosed why that can never be clean:** *"you take
 * a pic, slice it up… mesh does not compinsate the added surface area of the
 * model, so all added surface area is a hole."*
 *
 * **A generated body is a HOLLOW SHELL.** Cutting it makes two new faces and
 * nothing generates either, so the moment a joint moves you look through the cut
 * into the inside of the animal. There were **two holes at every joint** and
 * they needed different fixes — `capCuts` got the body from 2,280 open edges to
 * 18, and the gap *between* two closed parts could only be hidden by an overlap,
 * 🎯 *"something growing sideways out of the sheep."*
 *
 * ✅ **A skinned mesh is never cut.** The vertices near a joint are shared
 * between two bones and stretch between them. 🔴 **There is no socket, no stub,
 * no cap and no collar, and the failure mode CANNOT OCCUR.** Measured: a head at
 * its full 75° clamp shows no seam at all (`Design/samples/11*_rigged_*.png`).
 *
 * ## 📏 What it costs, measured rather than feared
 *
 * | | |
 * |---|---|
 * | **Posing ten sheep** | **0.2 ms of main thread**, median over 240 frames — against a 4 ms mark and a 16.7 ms frame |
 * | **Draw calls** | **one per sheep** |
 * | ⚠️ **The material** | **UNCHANGED.** `MeshLambertNodeMaterial` skins by itself — `NodeMaterial.setupPosition` applies `skinning()` to any `SkinnedMesh`. 🔴 **No new material path**, which is the trap that blacked out dev AND staging once |
 * | ⚠️ **Frame rate** | 🔴 **NOT MEASURED AND NOT MEASURABLE HERE.** No GPU on this workstation. haugur's F8 `Drawing` row is the instrument |
 */

import type { Bone, Object3D } from 'three';
import { Quaternion, Vector3 } from 'three';
import type { QuadAnatomy, RigAxes } from './quadrig.ts';

/** One bone, with everything needed to turn it about the animal's own axes. */
interface Bound {
  readonly bone: Bone;
  /** its rest rotation, so every pose starts from the rest pose */
  readonly rest: Quaternion;
  /** its rest offset from its parent — the neck STRETCHES by moving this (#820) */
  readonly restAt: Vector3;
  /** the animal's ACROSS axis, in this bone's parent space */
  readonly across: Vector3;
  /** the animal's UP axis, in this bone's parent space */
  readonly up: Vector3;
  /** the animal's LENGTH axis, in this bone's parent space — rolling into a turn */
  readonly length: Vector3;
}

export interface BoundRig {
  readonly legs: readonly {
    readonly bones: readonly Bound[];
    readonly fore: boolean;
    readonly side: 1 | -1;
  }[];
  readonly neck: readonly Bound[];
  readonly ears: readonly Bound[];
  readonly tail: readonly Bound[];
  /** pelvis → chest. Rolling these is what leans the body into a turn */
  readonly spine: readonly Bound[];
  /** 🔬 for an F8 report — what was actually found in the clone */
  readonly found: { legs: number; neck: number; ears: number; tail: number; spine: number };
}

/**
 * 🔴 **THE AXIS A BONE MUST TURN ABOUT IS NOT ITS OWN X, Y OR Z.**
 *
 * ⚠️ **This cost a baked picture.** The first version set `bone.rotation.x` and
 * the sheep came out contorted — legs splayed, body twisted — because **the rest
 * pose has 77 non-identity rotations**, so a leg's local X is not the animal's
 * across-axis and turning about it does not swing anything.
 *
 * ✅ **So compute, once at bind time, the animal's across and up axes expressed
 * in each bone's PARENT space.** Turning by a quaternion about that axis, and
 * pre-multiplying it onto the rest rotation, swings the bone about the animal's
 * own axis through its own origin — which is what a joint is.
 *
 * 🔴 **It walks the bone tree rather than reading world matrices**, so the answer
 * does not depend on where the clone happens to be standing or which way it is
 * facing. A world matrix would fold the animal's own yaw into the axis and the
 * legs would swing sideways as it turned.
 */
function bind(bone: Bone, stopAt: Object3D, axes: RigAxes): Bound {
  const parentRotation = new Quaternion();
  for (let node: Object3D | null = bone.parent; node && node !== stopAt; node = node.parent) {
    /* Pre-multiply walking UP, which composes parent-before-child. */
    parentRotation.premultiply(node.quaternion);
  }
  const inverse = parentRotation.clone().invert();
  const unit = (k: 0 | 1 | 2) => new Vector3(k === 0 ? 1 : 0, k === 1 ? 1 : 0, k === 2 ? 1 : 0);
  return {
    bone,
    rest: bone.quaternion.clone(),
    restAt: bone.position.clone(),
    across: unit(axes.across).applyQuaternion(inverse).normalize(),
    up: unit(axes.up).applyQuaternion(inverse).normalize(),
    length: unit(axes.length).applyQuaternion(inverse).normalize(),
  };
}

/**
 * 🦴 Find the anatomy's bones inside one cloned body.
 *
 * ⚠️ **`root` must be a `SkeletonUtils.clone`, never `Object3D.clone`** — a
 * plain clone SHARES the skeleton, so every sheep in the world would move as one
 * animal. 🔴 **That is a real difference from the cut version**, where
 * `template.clone(true)` was correct.
 */
export function bindRig(root: Object3D, anatomy: QuadAnatomy, order: readonly string[]): BoundRig {
  const byName = new Map<string, Bone>();
  root.traverse((o) => {
    if ((o as Bone).isBone === true) byName.set(o.name, o as Bone);
  });

  // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
  const get = (i: number): Bone | null => byName.get(order[i] ?? '') ?? null;
  const bindAll = (idx: readonly number[]): Bound[] =>
    idx
      .map(get)
      .filter((b): b is Bone => b !== null)
      .map((b) => bind(b, root, anatomy.axes));

  const legs = anatomy.legs.map((l) => ({
    bones: bindAll(l.bones),
    fore: l.fore,
    side: l.side,
  }));
  const neck = bindAll(anatomy.neck);
  const ears = bindAll(anatomy.ears);
  const tail = bindAll(anatomy.tail ?? []);
  const spine = bindAll(anatomy.spine);

  return {
    legs,
    neck,
    ears,
    tail,
    spine,
    found: {
      legs: legs.reduce((s, l) => s + l.bones.length, 0),
      neck: neck.length,
      ears: ears.length,
      tail: tail.length,
      spine: spine.length,
    },
  };
}

/**
 * 🦵 **How much the lower leg gives back, as a share of the hip's swing.**
 *
 * 🔴 **Negative on purpose.** The bones below the hip counter-rotate a little,
 * so the shin trails the thigh instead of swinging with it — which is what a
 * knee does, and the one thing Minecraft's single rigid box cannot show.
 * ⚠️ **A DIAL and not haugur's number.** Small: a leg that bends too much reads
 * as a dog, and this project's worst animal faults have all been overdone
 * motion rather than underdone.
 */
const KNEE = 0.35;

const spin = new Quaternion();

/** Turn one bone by `angle` about the animal's across (or up) axis. */
function turn(b: Bound, axis: 'across' | 'up' | 'length', angle: number): void {
  // eslint-disable-next-line security/detect-object-injection -- `axis` is a union of three literal keys, not user input.
  spin.setFromAxisAngle(b[axis], angle);
  b.bone.quaternion.copy(b.rest).premultiply(spin);
}

/** What a frame asks of the body. */
export interface RigPose {
  /** 🚶 the gait's own cosine, −1…+1, already scaled by speed */
  readonly swing: number;
  /** 👀 head yaw and pitch relative to the body, radians */
  readonly headYaw: number;
  readonly headPitch: number;
  /** 🐁 how much the tail swings, 0–1 */
  readonly tailWag: number;
  /**
   * 🔄 **How far the body ROLLS into a turn**, radians.
   *
   * 🎯 haugur asked for a lean (D124), and on the cut body it was a rotation of
   * the trunk GROUP. ⚠️ **There is no trunk group in a skinned mesh** — it is
   * one surface — so the lean rolls the SPINE bones and the skin follows.
   * 🔴 **It must never roll a leg**, or the hooves swing out sideways.
   */
  readonly lean: number;
  /** 🌿 how far down a grazing head is, 0 (up) … 1 (nose in the grass) — #820 */
  readonly graze?: number;
  /** 🌿 the chew, radians, on the SKULL only — never the whole neck (#820) */
  readonly chew?: number;
}

/**
 * 🌿 **HOW A SHEEP GETS ITS NOSE INTO THE GRASS — #820.**
 *
 * 🎯 **haugur, 2026-10-01:** *"A sheep grazing never reaches the ground, it bows
 * its head to chest heihgt and bobs around, head should reach the ground for it
 * to look convincing."*
 *
 * 📏 **WHY BENDING THE NECK FURTHER COULD NEVER DO IT — measured on the rig
 * sheet.** The neck's root sits **0.60 m** off the ground; neck plus head reach
 * about **0.38 m.** Bent straight down, the nose still hangs 20 cm up. 🔴 **This
 * generated body's neck is short** — a real ewe's neck and head are about as
 * long as her foreleg, and that is the whole reason she can graze standing.
 * ⚠️ **And the neck points 47° UP at rest**, so the old 64° of bend only got it
 * to 17° below level.
 *
 * ✅ **So four things move together**, and a full graze is all of them at once:
 *
 * | | |
 * |---|---|
 * | `dip` | the CHEST bone pitches down, so the shoulders drop — the forelegs hang off the chest and are turned back the same amount, so they stay upright under it |
 * | `neck` | the neck swings down **from the shoulder** — most of the bend, where a real one bends |
 * | `stretch` | the head moves out along the neck — a ewe's neck is an S at rest and **straightens** when she reaches down |
 * | `skull` | the skull turns back UP against the neck, so the muzzle points down-and-forward into the grass and does **not** fold under the chest (T267's old fault — and the first try at this one) |
 *
 * ⚠️ **DIALS, judged on the rig sheet**: `rigsheet.html?pose=graze` reports the
 * muzzle's height off the ground, and any of these can be overridden in its URL
 * (`&neck=1.4`) to try a value without a rebuild. `sheeprig.test.ts` pins the
 * muzzle within 6 cm of the grass on the real skeleton.
 */
export const GRAZE_BODY = {
  /** 📏 20° — the shoulders drop about 5 cm at the neck's root */
  dip: 0.35,
  /** 📏 92° from rest: the neck pointed 47° UP and now points about 65° down */
  neck: 1.6,
  /** 📏 the neck 80% longer — 0.18 m becomes 0.32. ⚠️ The biggest dial: a real neck straightens, but by less — this one makes up for a body whose neck is too short */
  stretch: 0.8,
  /** 📏 the skull back 30°, so it points about 80° down: muzzle in the grass, not under the chest */
  skull: -0.52,
};

/**
 * 🐑 **Pose the whole animal.** Called once per sheep per frame.
 *
 * 📐 **The gait is Mojang's** (`Minecraft/19` §1.1): one cosine, **diagonal
 * pairs in phase** — a fore leg moves with the OPPOSITE hind leg. ⚠️ Anything
 * else reads as a pantomime horse.
 *
 * 🔴 **The upper bone carries most of the stride and the hoof barely moves**,
 * which is what stops a leg reading as a stiff plank. It is a taper down the
 * chain rather than a per-bone dial, because **a dial per bone is a dial per
 * animal** and this has to work for a body nobody has looked at.
 */
export function poseRig(rig: BoundRig, pose: RigPose): void {
  for (const leg of rig.legs) {
    /* 📐 `+ − − +`: fore-left with hind-right. */
    const sign = leg.fore ? leg.side : -leg.side;
    const n = leg.bones.length;
    for (let k = 0; k < n; k++) {
      // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
      const b = leg.bones[k];
      if (b === undefined) continue;
      /**
       * 🔴 **THE WHOLE LEG SWINGS FROM ONE JOINT, AND THE REST BARELY MOVE.**
       *
       * ⚠️ **The obvious version was wrong and a baked picture caught it.** It
       * tapered the angle down the chain — most at the hip, least at the hoof —
       * expecting the hoof to move least. **Rotations COMPOUND down a bone
       * chain**, so every joint added to the one above it: 0.42 + 0.32 + 0.21 +
       * 0.11 ≈ **1.05 rad, 60°, and the sheep did the splits.**
       *
       * ✅ 📖 **Mojang's answer is one rigid leg on one joint**
       * (`Minecraft/19` §1.1) — a leg is a single box rotating at the hip. So
       * the root takes the swing and the joints below it take a small share for
       * a hint of a knee, which is the one thing a box cannot do.
       */
      const share = k === 0 ? 1 : -KNEE / (n - 1);
      turn(b, 'across', sign * pose.swing * share);
    }
  }

  /**
   * 👀 **THE YAW AND THE PITCH ARE SPREAD DIFFERENTLY DOWN THE NECK, AND THAT
   * IS ANATOMY RATHER THAN TASTE.**
   *
   * 🔴 **Turning to look at you is mostly the HEAD** — the skull swivels on the
   * top of the neck. ✅ So yaw is weighted toward the last bone.
   *
   * 🌿 **Reaching the grass is mostly the NECK** — an animal bends from the
   * shoulder and keeps its muzzle pointing down and forward. ⚠️ **Weighted the
   * other way round, the head folds under and the muzzle tucks back toward the
   * chest**, which is what the first baked graze showed. ✅ So pitch is
   * weighted toward the FIRST bone.
   *
   * 👉 ***One weighting for both looked right in a number and wrong in a
   * picture.***
   *
   * 🔴 **AND THE SHARES MUST SUM TO EXACTLY ONE.** ⚠️ **They were multiplied
   * by `n * 0.8` as well, so a two-bone neck turned 1.6× as far as it was
   * asked** — a 75° clamp became 120°. 🎯 **haugur saw it at once:** *"they
   * seldom look straight forward… so they are olways kinda looking to the
   * side, looks a little weird."* 👉 ***A clamp that something downstream
   * multiplies is not a clamp.***
   */
  const n = rig.neck.length;
  const total = (n * (n + 1)) / 2;
  for (let k = 0; k < n; k++) {
    // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
    const b = rig.neck[k];
    if (b === undefined) continue;
    /* toward the head for a turn, toward the shoulder for a bend */
    const yawShare = total > 0 ? (k + 1) / total : 0;
    const pitchShare = total > 0 ? (n - k) / total : 0;
    turn(b, 'up', pose.headYaw * yawShare);
    spin.setFromAxisAngle(b.across, pose.headPitch * pitchShare);
    b.bone.quaternion.premultiply(spin);
  }

  /* 🔄 The lean, on the spine only. ⚠️ Spread over the spine bones so the
     whole body banks rather than the chest snapping over on one joint. */
  for (const b of rig.spine) turn(b, 'length', pose.lean / Math.max(1, rig.spine.length));

  /* 🌿 The graze, LAST, on top of everything above — see GRAZE_BODY. */
  poseGraze(rig, Math.min(Math.max(pose.graze ?? 0, 0), 1), pose.chew ?? 0);

  /* 🐁 The tail, when the rig has one. `null` rigs simply have no tail bones,
     and nothing here invents one. */
  for (let k = 0; k < rig.tail.length; k++) {
    // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
    const b = rig.tail[k];
    if (b === undefined) continue;
    turn(b, 'across', pose.tailWag * 0.25 * (k + 1));
  }
}

/** Add a pitch (positive = front down) on top of whatever a bone already has. */
function pitch(b: Bound | undefined, angle: number): void {
  if (b === undefined || angle === 0) return;
  spin.setFromAxisAngle(b.across, angle);
  b.bone.quaternion.premultiply(spin);
}

/**
 * 🌿 **The graze — `g` of the way down.** Applied after the walk, the look and
 * the lean, because each of those RESETS its bones to rest first.
 *
 * 🔴 **The forelegs are turned back by exactly the dip.** They hang off the
 * chest, so tipping the chest would otherwise swing the hooves forward and lift
 * them 2 cm. Turning about the animal's own across axis is the same rotation in
 * every bone's frame, so the two cancel and the legs stay upright.
 */
function poseGraze(rig: BoundRig, g: number, chew: number): void {
  const head = rig.neck.at(-1);
  /* 🔴 Every frame from REST, or a stretch would compound frame on frame. */
  if (head !== undefined) head.bone.position.copy(head.restAt);
  if (g === 0 && chew === 0) return;

  const dip = GRAZE_BODY.dip * g;
  pitch(rig.spine.at(-1), dip);
  for (const leg of rig.legs) if (leg.fore) pitch(leg.bones[0], -dip);

  pitch(rig.neck[0], GRAZE_BODY.neck * g);
  if (head !== undefined && rig.neck.length > 1) {
    head.bone.position.multiplyScalar(1 + GRAZE_BODY.stretch * g);
  }
  pitch(head, GRAZE_BODY.skull * g + chew);
}
